import { QueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { paginatedSchema } from "../pagination";

import { layerCollectionSchema } from "./layercollections";
import { organisationSchema } from "./organisations";

export const wmsLayerListItemSchema = z.object({
  url: z.string(),
  uuid: z.string(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  slug: z.string(),
  get_feature_info: z.boolean(),
  min_zoom: z.number(),
  max_zoom: z.number(),
  tiled: z.boolean(),
  options: z.record(z.any()) /* So far seen: strings, numbers, booleans, objects... */,
  wms_url: z.string().nullable(),
  legend_url: z.string().nullable(),
  get_feature_info_url: z.string().nullable(),
  download_url: z.string().nullable(),
  organisation: organisationSchema,
  access_modifier: z.string(),
  supplier: z.string().nullable(),
  shared_with: z.array(organisationSchema),
  layer_collections: z.array(layerCollectionSchema),
  last_modified: lizardDateSchema.nullable(),
  created: lizardDateSchema,
  spatial_bounds: z
    .object({
      west: z.number(),
      east: z.number(),
      north: z.number(),
      south: z.number(),
    })
    .nullable(),
});

export type WmsLayerListItem = z.infer<typeof wmsLayerListItemSchema>;

export const wmsLayerListSchema = paginatedSchema(wmsLayerListItemSchema);
export type WmsLayerList = z.infer<typeof wmsLayerListSchema>;

export const wmsLayerSchema = wmsLayerListItemSchema;
export type WmsLayer = z.infer<typeof wmsLayerSchema>;

export function useWmsLayers(params = {}) {
  const url = combineUrlAndParams("/api/v4/wmslayers/", params);
  return useParsedQuery<WmsLayerList>(
    ["lizard-api-v4", "wmslayers", url],
    () => fetchWithError(url),
    (json: unknown) => wmsLayerListSchema.parse(json)
  );
}

export function useWmsLayer(uuid: string) {
  return useParsedQuery<WmsLayer>(
    ["lizard-api-v4", "wmslayers", uuid],
    () => fetchWithError(`/api/v4/wmslayers/${uuid}/`),
    (json: unknown) => wmsLayerSchema.parse(json)
  );
}

// Use this outside components, so the fetched WMS layer can still
// be stored in cache.
export async function fetchAndCacheWmsLayer(
  queryClient: QueryClient,
  uuid: string
): Promise<WmsLayer | null> {
  try {
    const response = await fetchWithError(`/api/v4/wmslayers/${uuid}/`);
    const wmsLayer = wmsLayerSchema.parse(response);

    queryClient.setQueryData(["lizard-api-v4", "wmslayers", uuid], wmsLayer);
    return wmsLayer;
  } catch (e) {
    return null;
  }
}

export function useMultipleWmsLayers(uuids: string[]) {
  return useParsedQueries(
    uuids.map((uuid) => {
      return {
        queryKey: ["lizard-api-v4", "wmslayers", uuid],
        fetchFunction: () => fetchWithError(`/api/v4/wmslayers/${uuid}/`),
        parseFunction: (json: unknown) => wmsLayerSchema.parse(json),
      };
    })
  );
}
